import { Outlet } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import AppSidebar from "@/components/layout/AppSidebar";
import UserMenu from "@/components/layout/UserMenu";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { CommandPalette } from "@/components/layout/CommandPalette";
import { KeyboardShortcutsDialog } from "@/components/layout/KeyboardShortcutsDialog";
import { useGlobalShortcuts } from "@/hooks/useGlobalShortcuts";
import { Button } from "@/components/ui/button";
import { Search, Keyboard } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { OverLimitBanner } from "@/components/billing/OverLimitBanner";
import { useMyNav } from "@/hooks/useMyNav";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import FeedbackWidget from "@/components/feedback/FeedbackWidget";
import { SupportModeBanner } from "@/components/admin/SupportModeBanner";

const AppLayout = () => {
  const { nav } = useMyNav();
  const { paletteOpen, setPaletteOpen, shortcutsOpen, setShortcutsOpen } =
    useGlobalShortcuts();
  const { user, session } = useAuth();
  const queryClient = useQueryClient();
  const acceptedRef = useRef<string | null>(null);

  // One-time per session: accept any pending manager + executive invites
  // for this email. Both endpoints are idempotent on the server.
  useEffect(() => {
    if (!user?.id || !session?.access_token) return;
    if (acceptedRef.current === user.id) return;
    acceptedRef.current = user.id;

    Promise.allSettled([
      supabase.functions.invoke("accept-manager-invites", {}),
      supabase.functions.invoke("accept-executive-invites", {}),
    ]).then((results) => {
      const [mgrRes, execRes] = results;
      if (mgrRes.status === "fulfilled" && (mgrRes.value as any).data?.accepted > 0) {
        queryClient.invalidateQueries({ queryKey: ["primary-org"] });
        queryClient.invalidateQueries({ queryKey: ["org-members"] });
      }
      if (execRes.status === "fulfilled" && (execRes.value as any).data?.accepted > 0) {
        queryClient.invalidateQueries({ queryKey: ["user-roles"] });
      }
    });
  }, [user?.id, session?.access_token, queryClient]);

  // Watchdog ping for managers, at most once per hour per browser, so a fully
  // stopped scheduler is still noticed. Emails go out only if a new alert exists.
  useEffect(() => {
    if (!nav?.is_manager || !session?.access_token) return;
    const KEY = "upbrief.jobHealthCheckedAt";
    const last = Number(localStorage.getItem(KEY) || 0);
    if (Date.now() - last < 60 * 60 * 1000) return;
    localStorage.setItem(KEY, String(Date.now()));
    supabase.rpc("check_job_health").then(({ data }) => {
      if ((data as { pending_email?: boolean } | null)?.pending_email) {
        supabase.functions.invoke("job-watchdog", {}).catch(() => {});
      }
    });
  }, [nav?.is_manager, session?.access_token]);


  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-2 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:shadow-md focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Skip to main content
        </a>
        <AppSidebar />
        <main className="flex-1 flex flex-col min-w-0">
          <SupportModeBanner />
          <header className="h-14 border-b border-border flex items-center justify-between px-3 sm:px-4 bg-card/50 sticky top-0 z-30 backdrop-blur">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="lg:hidden" />
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPaletteOpen(true)}
                    className="hidden sm:flex items-center gap-2 text-muted-foreground"
                  >
                    <Search className="w-4 h-4" />
                    <span>Search...</span>
                    <kbd className="ml-2 px-1.5 py-0.5 text-[10px] font-mono bg-muted border border-border rounded">
                      ⌘K
                    </kbd>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Quick search (⌘K)</TooltipContent>
              </Tooltip>
              <Button
                variant="ghost"
                size="icon"
                className="sm:hidden"
                onClick={() => setPaletteOpen(true)}
                aria-label="Search"
              >
                <Search className="w-5 h-5" />
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShortcutsOpen(true)}
                    aria-label="Keyboard shortcuts"
                    className="hidden sm:inline-flex"
                  >
                    <Keyboard className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Keyboard shortcuts (?)</TooltipContent>
              </Tooltip>
              <NotificationBell />
              <UserMenu />
            </div>
          </header>
          {nav.is_manager && <OverLimitBanner />}
          <div id="main-content" tabIndex={-1} className="flex-1 p-4 sm:p-6 overflow-x-hidden focus:outline-none">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <KeyboardShortcutsDialog
        open={shortcutsOpen}
        onOpenChange={setShortcutsOpen}
      />
      <FeedbackWidget />
    </SidebarProvider>
  );
};

export default AppLayout;
